/**
 * Layer 2 — Journeys. Complete operational business processes that span modules.
 * A journey resolves enabled only if its module and all step dependencies do.
 * Journeys are the future commercial packaging unit (a Journey Pack = a set of
 * `journey_pack`-scoped grants).
 */
import { MaturityLevel, defineJourney } from '@/lib/capabilities/contracts';

export const journeys = {
  'journeys.request_to_inspection': defineJourney({
    key: 'journeys.request_to_inspection',
    layer: 'journey',
    title: 'Request → inspection',
    description: 'Triage inbound demand and convert it into an inspection.',
    dependsOn: ['modules.requests', 'modules.inspections', 'capabilities.approvals'],
    provides: ['journey_request_to_inspection'],
    requires: ['module_requests', 'module_inspections', 'approvals'],
    minEdition: 'professional',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['journey'],
    active: true,
    moduleKey: 'modules.requests',
    steps: ['capabilities.approvals', 'capabilities.photo_capture'],
  }),
  'journeys.request_to_job': defineJourney({
    key: 'journeys.request_to_job',
    layer: 'journey',
    title: 'Request → job',
    description: 'Convert an approved request into a job.',
    dependsOn: ['modules.requests', 'modules.jobs', 'capabilities.approvals'],
    provides: ['journey_request_to_job'],
    requires: ['module_requests', 'module_jobs', 'approvals'],
    minEdition: 'professional',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['journey'],
    active: true,
    moduleKey: 'modules.requests',
    steps: ['capabilities.approvals'],
  }),
  'journeys.inspection_to_job': defineJourney({
    key: 'journeys.inspection_to_job',
    alwaysIncluded: 'Follows its modules: on when they are on.',
    layer: 'journey',
    title: 'Inspection → job',
    description: 'Raise follow-up jobs from failed inspection findings.',
    dependsOn: ['modules.inspections', 'modules.jobs'],
    provides: ['journey_inspection_to_job'],
    requires: ['module_inspections', 'module_jobs'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['journey'],
    active: true,
    moduleKey: 'modules.inspections',
    steps: [],
  }),
  'journeys.inspection_to_report': defineJourney({
    key: 'journeys.inspection_to_report',
    alwaysIncluded: 'Follows its modules: on when they are on.',
    layer: 'journey',
    title: 'Inspection → report',
    description: 'Produce a report from a completed inspection.',
    dependsOn: ['modules.inspections', 'modules.reports'],
    provides: ['journey_inspection_to_report'],
    requires: ['module_inspections', 'module_reports'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['journey'],
    active: true,
    moduleKey: 'modules.inspections',
    steps: [],
  }),
  'journeys.job_completion': defineJourney({
    key: 'journeys.job_completion',
    alwaysIncluded: 'Follows its modules: on when they are on.',
    layer: 'journey',
    title: 'Job completion',
    description: 'Complete, review and close out a job.',
    dependsOn: ['modules.jobs', 'modules.reviews'],
    provides: ['journey_job_completion'],
    requires: ['module_jobs', 'module_reviews'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['journey'],
    active: true,
    moduleKey: 'modules.jobs',
    steps: ['capabilities.approvals'],
  }),
  'journeys.asset_lifecycle': defineJourney({
    key: 'journeys.asset_lifecycle',
    alwaysIncluded: 'Follows its modules: on when they are on.',
    layer: 'journey',
    title: 'Asset lifecycle',
    description:
      'Commission an asset, take it out of service with a make-safe job, recommission it on an inspection whose findings become verified jobs, and keep the history of every move.',
    dependsOn: ['modules.assets', 'modules.inspections', 'modules.jobs', 'capabilities.notifications'],
    provides: ['journey_asset_lifecycle'],
    requires: ['module_assets'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['journey'],
    active: true,
    moduleKey: 'modules.assets',
    steps: ['capabilities.scheduling', 'capabilities.notifications'],
  }),
  'journeys.asset_onboarding': defineJourney({
    key: 'journeys.asset_onboarding',
    alwaysIncluded: 'Follows its modules: on when they are on.',
    layer: 'journey',
    title: 'Asset onboarding',
    description: 'Capture and register a new asset.',
    dependsOn: ['modules.assets', 'capabilities.photo_capture'],
    provides: ['journey_asset_onboarding'],
    requires: ['module_assets', 'evidence_media'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['journey'],
    active: true,
    moduleKey: 'modules.assets',
    steps: ['capabilities.photo_capture', 'capabilities.qr_codes'],
  }),
  'journeys.asset_retirement': defineJourney({
    key: 'journeys.asset_retirement',
    alwaysIncluded: 'Follows its modules: on when they are on.',
    layer: 'journey',
    title: 'Asset retirement',
    description:
      'Close the record for good: decommissioned first, no work in flight, a reason and a disposal record, shares and schedules ended by the database, a Retired lens on the list, admin-only restore.',
    dependsOn: ['modules.assets', 'capabilities.notifications'],
    provides: ['journey_asset_retirement'],
    requires: ['module_assets'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['journey'],
    active: true,
    moduleKey: 'modules.assets',
    steps: [],
  }),
  'journeys.preventive_maintenance': defineJourney({
    key: 'journeys.preventive_maintenance',
    alwaysIncluded: 'Follows its modules: on when they are on.',
    layer: 'journey',
    title: 'Preventive maintenance',
    description: 'Recurring, scheduled maintenance across assets.',
    dependsOn: ['modules.inspections', 'capabilities.recurring_work'],
    provides: ['journey_ppm'],
    requires: ['module_inspections', 'recurring_work'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['journey'],
    active: true,
    moduleKey: 'modules.inspections',
    steps: ['capabilities.scheduling', 'capabilities.recurring_work'],
  }),
  'journeys.reactive_maintenance': defineJourney({
    key: 'journeys.reactive_maintenance',
    alwaysIncluded: 'Follows its modules: on when they are on.',
    layer: 'journey',
    title: 'Reactive maintenance',
    description: 'Respond to breakdowns and ad-hoc issues.',
    dependsOn: ['modules.jobs', 'modules.requests'],
    provides: ['journey_reactive'],
    requires: ['module_jobs', 'module_requests'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['journey'],
    active: true,
    moduleKey: 'modules.jobs',
    steps: [],
  }),
  'journeys.compliance': defineJourney({
    key: 'journeys.compliance',
    alwaysIncluded: 'Follows its modules: on when they are on.',
    layer: 'journey',
    title: 'Compliance',
    description: 'Track certificates, documents and compliance status.',
    dependsOn: ['modules.reports', 'capabilities.evidence_intelligence'],
    provides: ['journey_compliance'],
    requires: ['module_reports', 'evidence_analysis'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['journey'],
    active: true,
    moduleKey: 'modules.reports',
    steps: [],
  }),
  'journeys.contractor_management': defineJourney({
    key: 'journeys.contractor_management',
    alwaysIncluded: 'Follows its modules: on when they are on.',
    layer: 'journey',
    title: 'Contractor management',
    description: 'Engage contractors and manage quotes end to end.',
    dependsOn: ['modules.contractors', 'capabilities.quotes'],
    provides: ['journey_contractor_management'],
    requires: ['module_contractors', 'quotes'],
    // The Matrix has no row for it on any plan; quotes (its dependency) decide per plan.
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['journey'],
    active: true,
    moduleKey: 'modules.contractors',
    steps: ['capabilities.quotes'],
  }),
  'journeys.customer_approval': defineJourney({
    key: 'journeys.customer_approval',
    layer: 'journey',
    title: 'Customer approval',
    description: 'Route work to a customer for approval and scheduling.',
    dependsOn: ['modules.requests', 'capabilities.approvals'],
    provides: ['journey_customer_approval'],
    requires: ['module_requests', 'approvals'],
    minEdition: 'professional',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['journey'],
    active: true,
    moduleKey: 'modules.requests',
    steps: ['capabilities.approvals'],
  }),
  'journeys.iot_alert_to_inspection': defineJourney({
    key: 'journeys.iot_alert_to_inspection',
    alwaysIncluded: 'Follows its modules: on when they are on.',
    layer: 'journey',
    title: 'IoT alert → inspection',
    description: 'Turn an IoT signal into an inspection automatically.',
    dependsOn: ['modules.inspections', 'capabilities.iot_connectors'],
    provides: ['journey_iot_alert_to_inspection'],
    requires: ['module_inspections', 'iot'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    // Released on the Business plan 2026-10-04 (plan rules: Admin -> Matrix).
    betaFlag: false,
    internalOnly: false,
    tags: ['journey'],
    active: true,
    moduleKey: 'modules.inspections',
    steps: ['capabilities.iot_connectors'],
  }),
} as const;
